import React, { useState } from 'react';
import { JobTrackingStatus } from '../api/jobTracking';

interface StatusOption {
  value: JobTrackingStatus;
  label: string;
  icon: string;
  color: string;
  bgColor: string;
  borderColor: string;
}

const STATUS_OPTIONS: StatusOption[] = [
  {
    value: JobTrackingStatus.SAVED,
    label: 'Saved',
    icon: '📌',
    color: 'text-white',
    bgColor: 'bg-amber-500',
    borderColor: 'border-amber-600',
  },
  {
    value: JobTrackingStatus.APPLIED,
    label: 'Applied',
    icon: '✅',
    color: 'text-blue-400',
    bgColor: 'bg-blue-900/20',
    borderColor: 'border-blue-700/30',
  },
  {
    value: JobTrackingStatus.INTERVIEW,
    label: 'Interview',
    icon: '📅',
    color: 'text-purple-400',
    bgColor: 'bg-purple-900/20',
    borderColor: 'border-purple-700/30',
  },
  {
    value: JobTrackingStatus.OFFER,
    label: 'Offer',
    icon: '🎁',
    color: 'text-green-400',
    bgColor: 'bg-green-900/20',
    borderColor: 'border-green-700/30',
  },
  {
    value: JobTrackingStatus.ACCEPTED,
    label: 'Accepted',
    icon: '🎉',
    color: 'text-emerald-400',
    bgColor: 'bg-emerald-900/20',
    borderColor: 'border-emerald-700/30',
  },
  {
    value: JobTrackingStatus.REJECTED,
    label: 'Rejected',
    icon: '❌',
    color: 'text-red-400',
    bgColor: 'bg-red-900/20',
    borderColor: 'border-red-700/30',
  },
  {
    value: JobTrackingStatus.WITHDRAWN,
    label: 'Withdrawn',
    icon: '⊘',
    color: 'text-gray-400',
    bgColor: 'bg-gray-900/20',
    borderColor: 'border-gray-700/30',
  },
];

interface StatusDropdownProps {
  currentStatus: JobTrackingStatus;
  onStatusChange: (status: JobTrackingStatus) => void;
  isUpdating?: boolean;
  onOpenChange?: (isOpen: boolean) => void;
}

export const StatusDropdown: React.FC<StatusDropdownProps> = ({
  currentStatus,
  onStatusChange,
  isUpdating = false,
  onOpenChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const currentOption =
    STATUS_OPTIONS.find((opt) => opt.value === currentStatus) || STATUS_OPTIONS[0];

  const handleToggle = () => {
    const newState = !isOpen;
    setIsOpen(newState);
    onOpenChange?.(newState);
  };

  const handleStatusClick = (status: JobTrackingStatus) => {
    if (status !== currentStatus) {
      onStatusChange(status);
    }
    setIsOpen(false);
    onOpenChange?.(false);
  };

  return (
    <div className="relative">
      <button
        className={`px-3 py-1.5 rounded-md text-sm font-medium transition-all ${currentOption.bgColor} ${currentOption.borderColor} ${currentOption.color} border hover:opacity-90 flex items-center gap-2 min-w-[140px] justify-between disabled:opacity-50 disabled:cursor-not-allowed`}
        onClick={(e) => {
          e.stopPropagation();
          handleToggle();
        }}
        disabled={isUpdating}
      >
        <span className="flex items-center gap-2">
          <span>{currentOption.icon}</span>
          {currentOption.label}
        </span>
        <svg
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="absolute left-0 top-full mt-2 w-48 bg-darkbrown-900/95 backdrop-blur-sm border border-cream-200/10 rounded-lg shadow-xl shadow-black/50"
          style={{ zIndex: 9999 }}
        >
          <div className="py-1">
            {STATUS_OPTIONS.map((option) => {
              const isSelected = option.value === currentStatus;

              return (
                <button
                  key={option.value}
                  className={`w-full text-left px-4 py-2 text-sm flex items-center gap-3 transition-colors
                    ${
                      isSelected
                        ? `${option.bgColor} ${option.color}`
                        : `hover:${option.bgColor} ${option.color} hover:bg-opacity-50`
                    }`}
                  onClick={() => handleStatusClick(option.value)}
                >
                  <span>{option.icon}</span>
                  <span>{option.label}</span>
                  {isSelected && (
                    <svg
                      className="w-4 h-4 ml-auto"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                    >
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export const getStatusBadgeClasses = (status: JobTrackingStatus): string => {
  const option = STATUS_OPTIONS.find((opt) => opt.value === status);
  if (!option) return 'bg-gray-900/20 text-gray-400 border-gray-700/30';
  return `${option.bgColor} ${option.color} ${option.borderColor}`;
};

export const getStatusColor = (status: JobTrackingStatus): string => {
  const option = STATUS_OPTIONS.find((opt) => opt.value === status);
  return option?.color || 'text-gray-400';
};
